import { useAuth } from '@/contexts/AuthContext';
import { createQuestionInfo, editQuestionInfo, ocrQuestion, useGetQuestionInfo, useGetTopicList, useGetKnowledgePointList, generateQuestion, generateQuestion2, recordQuestionChoice } from '@/services/generatedAPIs';
import { useSubjectList } from '@/hooks/use-subject-list';
import { getSubjectDisplayName } from '@/hooks/use-subject-name';
import { QuestionInfo } from '@/services/generatedAPIs.schemas';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@radix-ui/react-label';
import ContentLayout from '@/components/layout/ContentLayout';
import { CascadingSelect, type CascadingOption } from '@/components/cascading-select';
import { FileUp } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { useAsyncAction } from '@/hooks/use-async-action';
import { toast } from 'sonner';
import { LocalImage, parseImagesFromFilesJson, wrapDisplaylines, removeDisplaylines, newlinesToLatexBreaks, convertSpacesToLatex } from '@/utils/preprocess';
import { ImageUpload } from '@/components/ui/image-upload';
import { isEN } from '@/utils/i18n';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ROUTE } from '@/routes/constants';
import React from 'react';
import { difficultyLevelToText, gradeNumberToText } from '@/lib/utils';
import { AssignmentBasicInfo } from './AssignmentPage';
import { MCQAnswerEditor } from '@/components/ui/mcq-answer-editor';
import { type MCQOption, createDefaultMCQOptions, parseMultipleChoiceOptions, buildQuestionTextFromMCQ, buildAnswerTextFromMCQ } from '@/utils/mcq-utils';
import { MathfieldElement } from 'mathlive';
import { installMathFieldWrapper } from '@/components/readonly-question-display';

// Declare the math-field element type
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'math-field': any;
    }
  }
}

MathfieldElement.soundsDirectory = null;
MathfieldElement.keypressVibration = false;
MathfieldElement.fontsDirectory = null;

type QuestionEditPageProps = {
  isEditMode: boolean,
  topicId?: number,
  questionId?: number,
  defaultValue?: QuestionInfo,
  topicName?: string,
  isAIGenerate?: boolean,
  isAIGenerated?: boolean,
  resumeHomeworkState?: AssignmentBasicInfo & { questionInfoList?: any[] }
}

const QuestionEditPageInner = ({ state }: { state: QuestionEditPageProps }) => {
  const { t, i18n } = useTranslation();
  const { getCurrentUser } = useAuth();
  const navigate = useNavigate();

  const [questionText, setQuestionText] = useState(state.defaultValue?.questionText || "");
  const [answerText, setAnswerText] = useState(state.defaultValue?.standardAnswer || "");
  const [questionTextEn, setQuestionTextEn] = useState(state.defaultValue?.questionTextEn || "");
  const [answerTextEn, setAnswerTextEn] = useState(state.defaultValue?.standardAnswerEn || "");
  const [scope, setScope] = useState(state.defaultValue?.scope?.toString() || "2");
  const [grade, setGrade] = useState(state.defaultValue?.grade?.toString() || "7");
  const [questionType, setQuestionType] = useState(state.defaultValue?.questionType || "FRQ");
  const [language, setLanguage] = useState(() => {
    const locale = state.defaultValue?.locale;
    if (locale === 'e') return 'en';
    if (locale === 'c') return 'zh-HK';
    if (locale === 'b') return 'bilingual';
    return locale || "zh-HK";
  });
  const [difficulty, setDifficulty] = useState(state.defaultValue?.difficultyLevel?.toString() || "2");
  const [subjectId, setSubjectId] = useState<number>(0);

  // MCQ-specific state
  const [mcqOptions, setMcqOptions] = useState<MCQOption[]>(() => {
    if (state.defaultValue?.questionType === 'MCQ' && state.defaultValue?.questionText) {
      const parsed = parseMultipleChoiceOptions(state.defaultValue.questionText, state.defaultValue.standardAnswer || '');
      if (parsed) {
        // Also try to parse English options from questionTextEn
        const parsedEn = state.defaultValue?.questionTextEn
          ? parseMultipleChoiceOptions(state.defaultValue.questionTextEn, '')
          : null;
        if (parsedEn) {
          return parsed.options.map((opt, i) => ({
            ...opt,
            contentEn: parsedEn.options[i]?.content || '',
          }));
        }
        return parsed.options;
      }
    }
    return createDefaultMCQOptions();
  });
  const [mcqStem, setMcqStem] = useState(() => {
    if (state.defaultValue?.questionType === 'MCQ' && state.defaultValue?.questionText) {
      const parsed = parseMultipleChoiceOptions(state.defaultValue.questionText, '');
      if (parsed) return parsed.stem;
    }
    return state.defaultValue?.questionText || '';
  });
  const [mcqStemEn, setMcqStemEn] = useState(() => {
    if (state.defaultValue?.questionType === 'MCQ' && state.defaultValue?.questionTextEn) {
      const parsed = parseMultipleChoiceOptions(state.defaultValue.questionTextEn, '');
      if (parsed) return parsed.stem;
    }
    return state.defaultValue?.questionTextEn || '';
  });

  let questionInfoQuery = undefined;
  if (state.questionId) {
    questionInfoQuery = useGetQuestionInfo({ questionId: state.questionId! });
  }
  const questionInfo = questionInfoQuery?.data;

  // 获取主题和知识点数据
  const { data: topicData } = useGetTopicList({
    teacherId: getCurrentUser()!.id,
    schoolName: getCurrentUser()!.schoolName
  });

  // 根据当前 topicId 动态获取知识点（topicId 可选）
  const { data: knowledgePointData } = useGetKnowledgePointList({
    teacherId: getCurrentUser()!.id,
    schoolName: getCurrentUser()!.schoolName,
    topicId: state.topicId?.toString() ?? ''
  });

  // 获取科目列表
  const { subjectInfos } = useSubjectList();

  // 知识点选择状态
  const [selectedKnowledgePointIds, setSelectedKnowledgePointIds] = useState<string[]>([]);

  // Store newly created question ID to add to homework when resuming
  const [createdQuestionId, setCreatedQuestionId] = useState<number | null>(null);

  // 构建级联选择器的选项数据 - 直接使用 API 返回的知识点
  const cascadingOptions = React.useMemo((): CascadingOption[] => {
    if (!topicData?.data?.topicInfos || !knowledgePointData?.data?.knowledgePoints) {
      return [];
    }

    // 如果有指定 topicId，只显示该主题的知识点
    if (state.topicId) {
      const currentTopic = topicData.data.topicInfos.find(topic => topic.id === state.topicId);
      if (!currentTopic) {
        return [];
      }

      // 直接使用 API 返回的知识点（已经根据 topicId 过滤）
      const knowledgePoints = knowledgePointData.data.knowledgePoints.map(kp => ({
        label: isEN(i18n) ? (kp.enName || kp.name!) : kp.name!,
        value: kp.id!.toString(),
      }));

      if (knowledgePoints.length === 0) {
        return [];
      }

      return [{
        label: isEN(i18n) ? (currentTopic.enName || currentTopic.name!) : currentTopic.name!,
        value: currentTopic.id!.toString(),
        children: knowledgePoints
      }];
    }

    // 如果没有指定 topicId，显示所有主题及其知识点
    return topicData.data.topicInfos.map(topic => {
      const topicKnowledgePoints = knowledgePointData.data!.knowledgePoints
        .filter(kp => kp.mtrId && kp.mtrId.startsWith(topic.mtrId + '-'))
        .map(kp => ({
          label: isEN(i18n) ? (kp.enName || kp.name!) : kp.name!,
          value: kp.id!.toString(),
        }));

      return {
        label: isEN(i18n) ? (topic.enName || topic.name!) : topic.name!,
        value: topic.id!.toString(),
        children: topicKnowledgePoints
      };
    }).filter(topic => topic.children.length > 0); // 只显示有知识点的主题
  }, [topicData, knowledgePointData, state.topicId, i18n]);

  const { isLoading, execute } = useAsyncAction({ showErrorToast: false });
  const [questionImages, setQuestionImages] = useState<File[]>([]);
  const [standardAnswerImages, setAnswerImages] = useState<File[]>([]);

  const [imageUploadKey, setImageUploadKey] = useState<number>(0);

  // AI生成相关状态
  const [isAIGenerated, setIsAIGenerated] = useState(state.isAIGenerated ?? false); // 是否已经生成了题目
  const [isGenerating, setIsGenerating] = useState(false); // 是否正在生成中

  // 双题对比状态
  const [generatedQuestions, setGeneratedQuestions] = useState<{
    left: { question: string; answer: string; questionEn: string; answerEn: string; apiId: string } | null;
    right: { question: string; answer: string; questionEn: string; answerEn: string; apiId: string } | null;
  }>({ left: null, right: null });
  const [showComparison, setShowComparison] = useState(false); // 是否显示对比界面

  // 完成弹窗状态
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  // 在编辑模式下初始化知识点选择
  useEffect(() => {
    if (state.isEditMode && questionInfo?.data?.questionLabelList) {
      const kpIds = questionInfo.data.questionLabelList.map(label => label.id!.toString());
      setSelectedKnowledgePointIds(kpIds);
    }
  }, [state.isEditMode, questionInfo?.data?.questionLabelList]);

  // 在编辑模式下回显科目
  useEffect(() => {
    if (state.isEditMode && questionInfo?.data?.questionLabelList) {
      const subjectLabel = questionInfo.data.questionLabelList.find(label => label.type === 'subject');
      if (subjectLabel?.id) {
        setSubjectId(subjectLabel.id);
      }
    }
  }, [state.isEditMode, questionInfo?.data?.questionLabelList]);

  // 初始化默认科目
  useEffect(() => {
    if (subjectId === 0 && subjectInfos.length > 0) {
      setSubjectId(subjectInfos[0].id!);
    }
  }, [subjectInfos, subjectId]);

  useEffect(() => {
    installMathFieldWrapper();
  }, [isAIGenerated]);

  
  // Configure math-field with consistent behavior across all editable fields
  const configureMathField = useCallback((el: any) => {
    if (!el) return;
    el.mathModeSpace = '\\;';
    el.letterShapeStyle = 'upright';
    if (el.menuItems.length > 1) {
      el.menuItems = el.menuItems.filter((item: { id: string }) => item.id === 'mode');
    }
    el.addEventListener('keydown', (ev: KeyboardEvent) => {
      console.log('ev.key', ev.key);
      if (ev.key === '\\') {
        ev.preventDefault();
        el.executeCommand(['insert', '\\backslash']);
      } else if (ev.key === 'Escape') {
        ev.preventDefault();
      }
    }, { capture: true });
  }, []);

  // Sync mcqOptions + mcqStem → questionText / answerText
  useEffect(() => {
    console.log('questionType', questionType)
    if (questionType !== 'MCQ') return;

    const newQuestionText = buildQuestionTextFromMCQ(mcqStem, mcqOptions, 'zh');
    const newAnswerText = buildAnswerTextFromMCQ(mcqOptions);
    console.log('setQuestionText', newQuestionText)

    setQuestionText(newQuestionText);
    setAnswerText(newAnswerText);
  }, [mcqOptions, mcqStem, questionType]);

  // Sync mcqOptions + mcqStemEn → questionTextEn / answerTextEn
  useEffect(() => {
    if (questionType !== 'MCQ') return;

    const newQuestionTextEn = buildQuestionTextFromMCQ(mcqStemEn, mcqOptions, 'en');
    const newAnswerTextEn = buildAnswerTextFromMCQ(mcqOptions);

    setQuestionTextEn(newQuestionTextEn);
    setAnswerTextEn(newAnswerTextEn);
  }, [mcqOptions, mcqStemEn, questionType]);

  // Handle questionType changes: parse existing text when switching TO MCQ,
  // serialize MCQ state back when switching FROM MCQ
  const prevQuestionTypeRef = React.useRef(questionType);
  useEffect(() => {
    const prevType = prevQuestionTypeRef.current;
    prevQuestionTypeRef.current = questionType;

    console.log('prevType', prevType)
    if (prevType !== 'MCQ' && questionType === 'MCQ') {
      // Switching TO MCQ: try to parse existing questionText
      const parsed = parseMultipleChoiceOptions(questionText, answerText);
      if (parsed) {
        setMcqOptions(parsed.options);
        setMcqStem(parsed.stem);
      } else {
        setMcqOptions(createDefaultMCQOptions());
        setMcqStem(questionText);
      }
      // Parse English if available
      const parsedEn = parseMultipleChoiceOptions(questionTextEn, '');
      if (parsedEn) {
        setMcqOptions(prev => prev.map((opt, i) => ({
          ...opt,
          contentEn: parsedEn.options[i]?.content || '',
        })));
        setMcqStemEn(parsedEn.stem);
      } else {
        setMcqStemEn(questionTextEn);
      }
    }
  }, [questionType]); // eslint-disable-line react-hooks/exhaustive-deps

  // 处理题目选择的函数
  const handleQuestionSelect = async (selectedSide: 'A' | 'B') => {
    const selected = selectedSide === 'A' ? generatedQuestions.left : generatedQuestions.right;

    if (!selected) return;

    const subjectName = getSubjectDisplayName(subjectInfos, subjectId);
    if (!subjectName) {
      toast.error(t('common.loading') || 'Subject data is loading, please wait.');
      return;
    }

    try {
      await recordQuestionChoice({
        teacherId: getCurrentUser()!.id!,
        grade: parseInt(grade),
        subject: subjectName,
        subjectId: subjectId,
        language: language,
        questionType: questionType,
        topicId: state.topicId!,
        topicName: state.topicName!,
        knowledgePointIds: selectedKnowledgePointIds.join(','),
        questionA: {
          question: generatedQuestions.left!.question,
          answer: generatedQuestions.left!.answer,
          questionEn: generatedQuestions.left!.questionEn,
          answerEn: generatedQuestions.left!.answerEn,
          apiId: generatedQuestions.left!.apiId
        },
        questionB: {
          question: generatedQuestions.right!.question,
          answer: generatedQuestions.right!.answer,
          questionEn: generatedQuestions.right!.questionEn,
          answerEn: generatedQuestions.right!.answerEn,
          apiId: generatedQuestions.right!.apiId
        },
        selectedQuestion: selectedSide
      });
    } catch (error) {
      console.error('记录选择失败', error);
    }

    setQuestionText(selected.question);
    setAnswerText(selected.answer);
    setQuestionTextEn(selected.questionEn);
    setAnswerTextEn(selected.answerEn);

    // If MCQ, also populate MCQ-specific state
    if (questionType === 'MCQ') {
      const parsed = parseMultipleChoiceOptions(selected.question, selected.answer);
      if (parsed) {
        setMcqOptions(parsed.options);
        setMcqStem(parsed.stem);
      } else {
        setMcqOptions(createDefaultMCQOptions());
        setMcqStem(selected.question);
      }
      const parsedEn = parseMultipleChoiceOptions(selected.questionEn, '');
      if (parsedEn) {
        setMcqOptions(prev => prev.map((opt, i) => ({
          ...opt,
          contentEn: parsedEn.options[i]?.content || '',
        })));
        setMcqStemEn(parsedEn.stem);
      } else {
        setMcqStemEn(selected.questionEn);
      }
    }

    const hasChinese = !!selected.question;
    const hasEnglish = !!selected.questionEn;
    if (hasChinese && hasEnglish) setLanguage('bilingual');
    else if (hasEnglish) setLanguage('en');
    else setLanguage('zh-HK');

    setIsAIGenerated(true);
    setShowComparison(false);
    toast.success(t(selectedSide === 'A' ? 'teacher.selectedA' : 'teacher.selectedB'));
  };

  // AI生成题目的处理函数
  const handleAIGenerate = async () => {
    if (!state.topicId) {
      toast.error(t('common.fail') + ': 缺少主题ID');
      return;
    }


    if (!knowledgePointData?.data?.knowledgePoints) {
      toast.error(t('common.fail') + ': 缺少知识点数据');
      return;
    }

    //知识点非空检查
    if (selectedKnowledgePointIds.length === 0) {
      toast.error(t('common.fail') + ': 請先選擇知識點');
      return;
    }

    const subjectName = getSubjectDisplayName(subjectInfos, subjectId);
    if (!subjectName) {
      toast.error(t('common.loading') || 'Subject data is loading, please wait.');
      return;
    }

    setIsGenerating(true);

    try {
      const requestParams = {
        chatId: getCurrentUser()!.id!.toString(),
        prompt: JSON.stringify({
          grade: gradeNumberToText(parseInt(grade), i18n) || "中學一年級",
          questionType: questionType,
          locale: language,
          difficulty: difficultyLevelToText(parseInt(difficulty), i18n) || "中等",
          unit: [state.topicName],
          subject: subjectName,
          objectives: knowledgePointData.data.knowledgePoints.filter(kp => selectedKnowledgePointIds.includes(kp.id!.toString())).map(kp => kp.name).join(', '),
        })
      };

      // 并行调用两个API
      const [response1, response2] = await Promise.all([
        generateQuestion(requestParams),
        generateQuestion2(requestParams)
      ]);

      if (response1.code === 200 && response1.data?.question && response2.code === 200 && response2.data?.question) {
        setGeneratedQuestions({
          left: {
            question: response2.data.question || '',
            answer: response2.data.answer || '',
            questionEn: response2.data.questionEn || '',
            answerEn: response2.data.answerEn || '',
            apiId:'api2'
          },
          right: {
            question: response1.data.question || '',
            answer: response1.data.answer || '',
            questionEn: response1.data.questionEn || '',
            answerEn: response1.data.answerEn || '',
            apiId:'api1'
          }
        });

        // 显示对比界面
        setShowComparison(true);

        toast.success(t('common.success'));
      } else {
        toast.error(t('common.fail') + ': ' + (response1.message || response2.message));
      }
    } catch (error) {
      console.error("AI生成题目失败", error);
      toast.error(t('common.fail'));
    } finally {
      setIsGenerating(false);
    }
  };


  // 监听 questionInfo 变化，每次获取数据后刷新 ImageUpload 组件
  useEffect(() => {
    if (questionInfo?.data) {
      // 每次 questionInfo 数据更新时，增加 key 值来强制刷新 ImageUpload 组件
      setImageUploadKey(prev => prev + 1);
    }
  }, [questionInfo?.data]);



  // 题目图片变化回调
  const handleQuestionImagesChange = useCallback((_: File[], allImages: any[]) => {
    // In edit mode, we need to send all images (existing + new)
    // Convert all images to File objects for the API
    const allFiles = allImages.map(img => img.file);
    setQuestionImages(allFiles);
  }, []);

  // 答案图片变化回调
  const handleAnswerImagesChange = useCallback((_: File[], allImages: any[]) => {
    // In edit mode, we need to send all images (existing + new)
    // Convert all images to File objects for the API
    const allFiles = allImages.map(img => img.file);
    setAnswerImages(allFiles);
  }, []);



  // 使用 useMemo 缓存初始图片数据，避免无限循环
  const initialQuestionImages = useMemo((): LocalImage[] => {
    if (!state.isEditMode || !questionInfo?.data?.question) {
      return [];
    }

    return parseImagesFromFilesJson(questionInfo.data.question.filesJson, 'question');
  }, [state.isEditMode, questionInfo?.data?.question?.filesJson]);

  const initialAnswerImages = useMemo((): LocalImage[] => {
    if (!state.isEditMode || !questionInfo?.data?.question) {
      return [];
    }

    return parseImagesFromFilesJson(questionInfo.data.question.filesJson, 'standardAnswer');
  }, [state.isEditMode, questionInfo?.data?.question?.filesJson]);

  // Check if all required fields are filled based on language
  const isFormComplete = Boolean(
    state.topicId &&
    (questionType === 'MCQ'
      ? (() => {
          const hasCorrectAnswer = mcqOptions.some(o => o.isCorrect);
          const hasStem = language === 'en'
            ? mcqStemEn.trim()
            : language === 'zh-HK'
              ? mcqStem.trim()
              : mcqStem.trim() && mcqStemEn.trim();
          const hasOptions = language === 'en'
            ? mcqOptions.some(o => o.contentEn.trim())
            : language === 'zh-HK'
              ? mcqOptions.some(o => o.content.trim())
              : mcqOptions.some(o => o.content.trim()) && mcqOptions.some(o => o.contentEn.trim());
          return hasStem && hasOptions && hasCorrectAnswer;
        })()
      : (language === 'zh-HK'
          ? questionText.trim() && answerText.trim()
          : language === 'en'
            ? questionTextEn.trim() && answerTextEn.trim()
            : language === 'bilingual'
              ? questionText.trim() && answerText.trim() && questionTextEn.trim() && answerTextEn.trim()
              : false))
  );

  // Validate language matches field existence
  const validateLanguageFields = (): boolean => {
    if (questionType === 'MCQ') {
      // MCQ-specific validation
      const hasCorrectAnswer = mcqOptions.some(o => o.isCorrect);
      if (!hasCorrectAnswer) {
        toast.error(t('teacher.alert') + ': Please select a correct answer');
        return false;
      }
      if (language === 'zh-HK') {
        if (!mcqStem.trim() || !mcqOptions.some(o => o.content.trim())) {
          toast.error(t('teacher.alert') + ': ' + t('common.question') + '/' + t('common.answer') + ' (' + t('teacher.zh') + ')');
          return false;
        }
      } else if (language === 'en') {
        if (!mcqStemEn.trim() || !mcqOptions.some(o => o.contentEn.trim())) {
          toast.error(t('teacher.alert') + ': ' + t('common.question') + '/' + t('common.answer') + ' (' + t('teacher.en') + ')');
          return false;
        }
      } else if (language === 'bilingual') {
        if (!mcqStem.trim() || !mcqOptions.some(o => o.content.trim())) {
          toast.error(t('teacher.alert') + ': ' + t('common.question') + '/' + t('common.answer') + ' (' + t('teacher.zh') + ')');
          return false;
        }
        if (!mcqStemEn.trim() || !mcqOptions.some(o => o.contentEn.trim())) {
          toast.error(t('teacher.alert') + ': ' + t('common.question') + '/' + t('common.answer') + ' (' + t('teacher.en') + ')');
          return false;
        }
      }
      return true;
    }

    // FRQ/BFQ validation (original logic)
    if (language === 'zh-HK') {
      if (!questionText.trim() || !answerText.trim()) {
        toast.error(t('teacher.alert') + ': ' + t('common.question') + '/' + t('common.answer') + ' (' + t('teacher.zh') + ')');
        return false;
      }
    } else if (language === 'en') {
      if (!questionTextEn.trim() || !answerTextEn.trim()) {
        toast.error(t('teacher.alert') + ': ' + t('common.question') + '/' + t('common.answer') + ' (' + t('teacher.en') + ')');
        return false;
      }
    } else if (language === 'bilingual') {
      if (!questionText.trim() || !answerText.trim()) {
        toast.error(t('teacher.alert') + ': ' + t('common.question') + '/' + t('common.answer') + ' (' + t('teacher.zh') + ')');
        return false;
      }
      if (!questionTextEn.trim() || !answerTextEn.trim()) {
        toast.error(t('teacher.alert') + ': ' + t('common.question') + '/' + t('common.answer') + ' (' + t('teacher.en') + ')');
        return false;
      }
    }
    return true;
  };

  const onSubmit = () => {
    if (!state.topicId) {
      return;
    }

    if (!subjectId || subjectId <= 0) {
      alert(t('teacher.alert') + t('common.subject'));
      return;
    }

    // Validate language matches field existence
    if (!validateLanguageFields()) {
      return;
    }

    if (state.isEditMode && state.defaultValue!.creatorId === getCurrentUser()!.email) {
      if (!state.questionId) {
        return;
      }

      execute(async () => {
        const res = await editQuestionInfo({
          model: JSON.stringify({
            questionInfoId: state.questionId,
            questionInfo: {
              question: questionText,
              answer: answerText,
              questionEn: questionTextEn,
              answerEn: answerTextEn,
              grade: parseInt(grade),
              questionType: questionType,
              originName: getCurrentUser()!.schoolName,
              originType: "school",
              locale: language,
              difficulty: parseInt(difficulty),
              topicIds: [state.topicId],
              subjectId: subjectId,
              knowledgePointIds: selectedKnowledgePointIds.map(value => parseInt(value)),
              version: state.defaultValue?.version ?? 0,
              scope: parseInt(scope),
            }
          }),
          questionImages: questionImages,
          standardAnswerImages: standardAnswerImages,
          answerImages: [],
        });
        if (res.code === 200) {
          navigate(-1);
          alert(t('common.edit') + t('common.success'));
        }
        else {
          console.error("Edit question failed", res.code, res.message);
          alert(t('common.edit') + t('common.fail'));
        }
      });
    } else {
      execute(async () => {
        const res = await createQuestionInfo({
          model: JSON.stringify({
            question: questionText,
            answer: answerText,
            questionEn: questionTextEn,
            answerEn: answerTextEn,
            grade: parseInt(grade),
            questionType: questionType,
            originName: getCurrentUser()!.schoolName,
            originType: "school",
            locale: language,
            difficulty: parseInt(difficulty),
            topicIds: [state.topicId],
            subjectId: subjectId,
            knowledgePointIds: selectedKnowledgePointIds.map(value => parseInt(value)),
            version: 0,
            scope: parseInt(scope),
          }),
          questionImages: questionImages,
          standardAnswerImages: standardAnswerImages,
          answerImages: [],
        });
        if (res.code === 200) {
          console.log("Create question successful", res.data);
          if (res.data?.id) {
            setCreatedQuestionId(res.data.id);
          }
          setShowSuccessDialog(true);
        }
        else {
          console.error("Create question failed", res.code, res.message);
          toast.error(t('common.edit') + t('common.fail'));
        }
      });
    }
  };

  const handleFile = (file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    // OCR question successful {"status": true, "res": {"type": "formula", "info": "1.\\quad\\text{Arrange the following numbers in ascending order.}\\\\-\\frac{1}{5},\\:\\frac{2}{3},\\:-0.5,\\:0.6,\\:\\frac{5}{7}", "conf": 0.9387915730476379}, "request_id": "tr_17470632750221802612596940575"}
    execute(async () => {
      const res = await ocrQuestion(formData as any, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      if (res.code === 200 && res.data) {
        const ocrRsp = JSON.parse(res.data);
        if (ocrRsp.status) {
          setQuestionText(ocrRsp.res.info);
        } else {
          toast.error(t('common.ocr') + t('common.fail') + res.message);
          console.error("OCR question failed", res.code, res.message);
        }
      }
      else {
        toast.error(t('common.ocr') + t('common.fail') + res.message);
        console.error("OCR question failed", res.code, res.message);
      }
    });
  }

  return (
    <ContentLayout title={state.isEditMode ? t("teacher.editquestion") : t("teacher.createquestion")}>
      <div className="flex flex-col bg-background p-12">
        <div className="flex flex-col w-full bg-muted place-self-center rounded-2xl border-2 border-black-300 gap-2 text-center pb-8 px-8 pt-4">
          <Label className='text-4xl pt-2 p-2'>{t("common.unit")}: {state.topicName}</Label>
          <div className='flex flex-row place-self-center items-center justify-center'>
            <Button variant="ghost">
              <Label htmlFor="fileInput" className='flex flex-row h-full w-full text-xl font-normal text-pink-600 items-center justify-center text-nowrap'><FileUp></FileUp>{t("component.importfromimg")}</Label>
              <Input id="fileInput" className='w-0 h-0 hidden' type="file" accept="image/*" onChange={(e) => e.target.files && handleFile(e.target.files[0])} />
            </Button>
          </div>
          <div id="questionEditor" />
          <form onSubmit={(e) => { e.preventDefault(); }} className="overflow-y-auto overflow-x-hidden">
            {/* <DialogDescription>
            { isEditMode ? "编辑题目" : "创建题目" }
            </DialogDescription> */}
            <div className="grid gap-4 py-6 px-4">
              {/* 6个表单项 - 移到顶部 */}
              <div className="grid gap-4">
                <div className="space-y-4">
                  {/* 第一行 */}
                  <div className="grid grid-cols-[1fr_1fr_1fr] gap-6">
                    <div className="grid grid-cols-[80px_1fr] items-center gap-4">
                      <Label htmlFor="scope" className="text-right">
                        {t("common.scope")}
                      </Label>
                      <Select name="scope" defaultValue={scope} onValueChange={setScope}>
                        <SelectTrigger className="w-[160px]">
                          <SelectValue placeholder="Select a scope" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectItem value="1">{t('teacher.scope1')}</SelectItem>
                            <SelectItem value="2">{t('teacher.scope2')}</SelectItem>
                            <SelectItem value="3">{t('teacher.scope3')}</SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-[80px_1fr] items-center gap-4">
                      <Label htmlFor="grade" className="text-right">
                        {t("common.grade")}
                      </Label>
                      <Select name="grade" defaultValue={grade} onValueChange={setGrade}>
                        <SelectTrigger className="w-[160px]">
                          <SelectValue placeholder="Select a grade" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectItem value="7">{t("teacher.s1")}</SelectItem>
                            <SelectItem value="8">{t("teacher.s2")}</SelectItem>
                            <SelectItem value="9">{t("teacher.s3")}</SelectItem>
                            <SelectItem value="10">{t("teacher.s4")}</SelectItem>
                            <SelectItem value="11">{t("teacher.s5")}</SelectItem>
                            <SelectItem value="12">{t("teacher.s6")}</SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-[80px_1fr] items-center gap-4 justify-self-end">
                      <Label htmlFor="language" className="text-right">
                        {t("common.language")}
                      </Label>
                      <Select name="language" value={language} onValueChange={setLanguage}>
                        <SelectTrigger className="w-[160px]">
                          <SelectValue placeholder="Select a language" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectItem value="en">{t('teacher.en')}</SelectItem>
                            <SelectItem value="zh-HK">{t('teacher.zh')}</SelectItem>
                            <SelectItem value="bilingual">{t('common.bilingual')}</SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* 第二行 */}
                  <div className="grid grid-cols-[1fr_1fr_1fr] gap-6">
                    <div className="grid grid-cols-[80px_1fr] items-center gap-4">
                      <Label htmlFor="questionType" className="text-right">
                        {t("teacher.qtype")}
                      </Label>
                      <Select name="questionType" defaultValue={questionType} onValueChange={setQuestionType}>
                        <SelectTrigger className="w-[160px]">
                          <SelectValue placeholder="Select a questionType" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectItem value="BFQ">{t("teacher.bfq")}</SelectItem>
                            <SelectItem value="MCQ">{t("teacher.mcq")}</SelectItem>
                            <SelectItem value="FRQ">{t("teacher.frq")}</SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-[80px_1fr] items-center gap-4">
                      <Label htmlFor="difficulty" className="text-right">
                        {t("common.difficulty")}
                      </Label>
                      <Select name="difficulty" defaultValue={difficulty} onValueChange={setDifficulty}>
                        <SelectTrigger className="w-[160px]">
                          <SelectValue placeholder="Select a difficulty" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectItem value="1">{t("common.easy")}</SelectItem>
                            <SelectItem value="2">{t("common.median")}</SelectItem>
                            <SelectItem value="3">{t("common.hard")}</SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-[80px_1fr] items-center gap-4 justify-self-end">
                      <Label htmlFor="subject" className="text-right">
                        {t("common.subject")}
                      </Label>
                      <Select name="subject" value={subjectId.toString()} onValueChange={(v) => setSubjectId(Number(v))}>
                        <SelectTrigger className="w-[160px]">
                          <SelectValue placeholder="Select a subject" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {subjectInfos.map((label) => (
                              <SelectItem key={label.id} value={label.id!.toString()}>
                                {isEN(i18n) ? (label.enName || label.name) : label.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              {/* 知识点选择 */}
              <div className="grid grid-cols-[80px_1fr] items-center gap-4">
                <Label htmlFor="knowledgePoints" className="text-right">
                  {t("common.knowledgepoint")}
                </Label>
                <CascadingSelect
                  className="w-full"
                  options={cascadingOptions}
                  value={selectedKnowledgePointIds}
                  onChange={setSelectedKnowledgePointIds}
                  placeholder={t('component.selectknowledgepoint')}
                />
              </div>

              {/* AI生成按钮 - 只在AI生成模式且未生成时显示 */}
              {state.isAIGenerate && !isAIGenerated && !showComparison && (
                <div className="flex justify-center py-6">
                  <Button
                    onClick={handleAIGenerate}
                    disabled={isGenerating}
                    className="bg-chart-4 hover:bg-chart-4/90 text-white px-8 py-2"
                  >
                    {isGenerating ? t('common.submitting') : t('teacher.aigen')}
                  </Button>
                </div>
              )}

              {/* 双题对比界面 - AI生成后显示 */}
              {showComparison && (
                <div className="space-y-4 py-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-px flex-1 bg-gray-200" />
                    <span className="text-lg font-semibold text-gray-500 px-2">{t("teacher.SelectQuestionsDesc")}</span>
                    <div className="h-px flex-1 bg-gray-200" />
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    {/* 左侧题目 A */}
                    <div className="border-2 border-gray-200 rounded-lg p-6 space-y-4 bg-gray-50/50 shadow-md hover:shadow-lg transition-shadow hover:scale-[1.01] transition-transform ">
                      {/* 左卡顶部 */}
                      <div className="flex items-center gap-2 mb-2 ">
                            <span className="bg-primary text-white text-s font-bold px-3 py-1 rounded-full">A</span>
                            <span className="text-sm font-medium text-gray-700">{t("common.question")} </span>
                      </div>

                      <div className="space-y-3">
                        <div className="border rounded-md p-4 bg-white min-h-[80px] text-left shadow-sm">
                          <div read-only className="w-full break-words">
                            {language === 'zh-HK' ? generatedQuestions.left?.question : generatedQuestions.left?.questionEn}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="text-sm font-medium text-left text-gray-600">{t("common.answer")}：</div>
                        <div className="border rounded-md p-4 bg-white min-h-[80px] text-left shadow-sm">
                          <div read-only className="w-full break-words">
                            {language === 'zh-HK' ? generatedQuestions.left?.answer : generatedQuestions.left?.answerEn}
                          </div>
                        </div>
                      </div>

                        <Button
                          className="bg-primary"
                          onClick={() => handleQuestionSelect('A')}
                        >
                          {t("teacher.selectThisQuestion")}
                        </Button>
                    </div>

                    {/* 右侧题目 B */}
                    <div className="border-2 border-gray-200 rounded-lg p-6 space-y-4 bg-gray-50/50 shadow-md hover:shadow-lg transition-shadow hover:scale-[1.01] transition-transform ">
                      <div className="flex items-center gap-2 mb-2 ">
                            <span className="bg-primary text-white text-s font-bold px-3 py-1 rounded-full">B</span>
                            <span className="text-sm font-medium text-gray-700">{t("common.question")}</span>
                      </div>
                      <div className="space-y-3">
                        <div className="border rounded-md p-4 bg-white min-h-[80px] text-left shadow-sm">
                          <div read-only className="w-full break-words">
                            {language === 'zh-HK' ? generatedQuestions.right?.question : generatedQuestions.right?.questionEn}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="text-sm font-medium text-left text-gray-600">{t("common.answer")}：</div>
                        <div className="border rounded-md p-4 bg-white min-h-[80px] text-left shadow-sm">
                          <div read-only className="w-full break-words">
                            {language === 'zh-HK' ? generatedQuestions.right?.answer : generatedQuestions.right?.answerEn}
                          </div>
                        </div>
                      </div>

                      <Button
                        className=" bg-primary"
                        onClick={() => handleQuestionSelect('B')}
                      >
                        {t("teacher.selectThisQuestion")}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* 题目和答案部分 - 在非AI生成模式或已生成后显示 */}
              {(!state.isAIGenerate || isAIGenerated) && (
                <div className="space-y-6">
                  {/* 中文题目输入框 - 当语言为中文或双语时显示 */}
                  {(language === 'zh-HK' || language === 'bilingual') && (
                    <>
                      <div className="grid grid-cols-[80px_1fr] items-center gap-4">
                        <Label htmlFor="question" className="text-right">
                          {t("common.question") + (language === 'bilingual' ? ' (' + t('teacher.zh') + ')' : '')}
                        </Label>
                        <math-field className="w-full border-gray-200 border-2 rounded-md"
                          ref={configureMathField}
                          onInput={(evt: any) => {
                            const input = removeDisplaylines(evt.target.value);
                            if (questionType === 'MCQ') {
                              setMcqStem(input);
                            } else {
                              setQuestionText(input);
                            }
                          }}
                        >
                          {wrapDisplaylines(newlinesToLatexBreaks(convertSpacesToLatex(questionType === 'MCQ' ? mcqStem : questionText)))}
                        </math-field>
                      </div>

                      {/* 题目图片上传 - 独立的区域 */}
                      <div className="grid grid-cols-[80px_1fr] items-center gap-4">
                        <div></div> {/* 空白占位，保持对齐 */}
                        {/* 只有在数据加载完成后才渲染图片上传组件 */}
                        {(!state.isEditMode || (questionInfo?.data && !questionInfoQuery?.isLoading)) && (
                          <ImageUpload
                            key={`question-${imageUploadKey}-${state.isEditMode ? state.questionId : 'new'}`}
                            onImagesChange={handleQuestionImagesChange}
                            maxImages={8}
                            initialImages={initialQuestionImages}
                            isEditMode={state.isEditMode}
                            imageType="question"
                          />
                        )}
                      </div>
                    </>
                  )}

                  {/* 英文题目输入框 - 当语言为英文或双语时显示 */}
                  {(language === 'en' || language === 'bilingual') && (
                    <>
                      <div className="grid grid-cols-[80px_1fr] items-center gap-4">
                        <Label htmlFor="questionEn" className="text-right">
                          {t("common.question") + (language === 'bilingual' ? ' (' + t('teacher.en') + ')' : '')}
                        </Label>
                        <math-field className="w-full border-gray-200 border-2 rounded-md"
                          ref={configureMathField}
                          onInput={(evt: any) => {
                            const input = removeDisplaylines(evt.target.value);
                            if (questionType === 'MCQ') {
                              setMcqStemEn(input);
                            } else {
                              setQuestionTextEn(input);
                            }
                          }}
                        >
                          {wrapDisplaylines(newlinesToLatexBreaks(convertSpacesToLatex(questionType === 'MCQ' ? mcqStemEn : questionTextEn)))}
                        </math-field>
                      </div>

                      {/* 英文题目图片上传 - 仅在非双语模式下显示（双语模式下图片共用） */}
                      {language !== 'bilingual' && (
                        <div className="grid grid-cols-[80px_1fr] items-center gap-4">
                          <div></div>
                          {(!state.isEditMode || (questionInfo?.data && !questionInfoQuery?.isLoading)) && (
                            <ImageUpload
                              key={`question-${imageUploadKey}-${state.isEditMode ? state.questionId : 'new'}`}
                              onImagesChange={handleQuestionImagesChange}
                              maxImages={8}
                              initialImages={initialQuestionImages}
                              isEditMode={state.isEditMode}
                              imageType="question"
                            />
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* 中文答案输入框 - 当语言为中文或双语时显示 */}
                  {(language === 'zh-HK' || language === 'bilingual') && (
                    <>
                      {questionType === 'MCQ' ? (
                        <div className="grid grid-cols-[80px_1fr] items-start gap-4">
                          <Label className="text-right pt-1">
                            {t("common.answer") + (language === 'bilingual' ? ' (' + t('teacher.zh') + ')' : '')}
                          </Label>
                          <MCQAnswerEditor
                            options={mcqOptions}
                            onOptionsChange={setMcqOptions}
                            lang={'zh-HK'}
                          />
                        </div>
                      ) : (
                        <div className="grid grid-cols-[80px_1fr] items-center gap-4">
                          <Label htmlFor="answer" className="text-right">
                            {t("common.answer") + (language === 'bilingual' ? ' (' + t('teacher.zh') + ')' : '')}
                          </Label>
                          <math-field className="w-full border-gray-200 border-2 rounded-md"
                            ref={configureMathField}
                            onInput={(evt: any) => {
                              setAnswerText(removeDisplaylines(evt.target.value));
                            }}
                          >
                            {wrapDisplaylines(newlinesToLatexBreaks(convertSpacesToLatex(answerText)))}
                          </math-field>
                        </div>
                      )}

                      {/* 答案图片上传 - 独立的区域 */}
                      <div className="grid grid-cols-[80px_1fr] items-center gap-4">
                        <div></div> {/* 空白占位，保持对齐 */}
                        {/* 只有在数据加载完成后才渲染图片上传组件 */}
                        {(!state.isEditMode || (questionInfo?.data && !questionInfoQuery?.isLoading)) && (
                          <ImageUpload
                            key={`answer-${imageUploadKey}-${state.isEditMode ? state.questionId : 'new'}`}
                            onImagesChange={handleAnswerImagesChange}
                            maxImages={8}
                            initialImages={initialAnswerImages}
                            isEditMode={state.isEditMode}
                            imageType="standardAnswer"
                          />
                        )}
                      </div>
                    </>
                  )}

                  {/* 英文答案输入框 - 当语言为英文或双语时显示 */}
                  {(language === 'en' || language === 'bilingual') && (
                    <>
                      {questionType === 'MCQ' ? (
                        <div className="grid grid-cols-[80px_1fr] items-start gap-4">
                          <Label className="text-right pt-1">
                            {t("common.answer") + (language === 'bilingual' ? ' (' + t('teacher.en') + ')' : '')}
                          </Label>
                          <MCQAnswerEditor
                            options={mcqOptions}
                            onOptionsChange={setMcqOptions}
                            lang={'en'}
                            showCheckbox={language !== 'bilingual'}
                          />
                        </div>
                      ) : (
                        <div className="grid grid-cols-[80px_1fr] items-center gap-4">
                          <Label htmlFor="answerEn" className="text-right">
                            {t("common.answer") + (language === 'bilingual' ? ' (' + t('teacher.en') + ')' : '')}
                          </Label>
                          <math-field className="w-full border-gray-200 border-2 rounded-md"
                            ref={configureMathField}
                            onInput={(evt: any) => {
                              setAnswerTextEn(removeDisplaylines(evt.target.value));
                            }}
                          >
                            {wrapDisplaylines(newlinesToLatexBreaks(convertSpacesToLatex(answerTextEn)))}
                          </math-field>
                        </div>
                      )}

                      {/* 英文答案图片上传 - 仅在非双语模式下显示（双语模式下图片共用） */}
                      {language !== 'bilingual' && (
                        <div className="grid grid-cols-[80px_1fr] items-center gap-4">
                          <div></div>
                          {(!state.isEditMode || (questionInfo?.data && !questionInfoQuery?.isLoading)) && (
                            <ImageUpload
                              key={`answer-${imageUploadKey}-${state.isEditMode ? state.questionId : 'new'}`}
                              onImagesChange={handleAnswerImagesChange}
                              maxImages={8}
                              initialImages={initialAnswerImages}
                              isEditMode={state.isEditMode}
                              imageType="standardAnswer"
                            />
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* <Label htmlFor="tags" className="text-right">
                tags，以逗号隔开
              </Label>
              <Input name="tags" className="col-span-3" /> */}
              {/*
                <Label htmlFor="knowledge_points" className="text-right">
                  知识点
                </Label>
                <MultiSelect
                  className="col-span-3"
                  options={knowledgePointRsp?.data?.questionLabels.map((label) => { return { value: label.id!.toString(), label: isEN(i18n) ? label.enName! : label.name! } }) ?? []}
                  onValueChange={setKnowledgePoints}
                  defaultValue={knowledgePoints}
                  placeholder="Select knowledge points"
                  variant="default"
                /> */}
            </div>

            <Separator className="my-4" ></Separator>
            {/* 按钮区域 - 与上方表单保持相同的布局结构 */}
            <div className="grid grid-cols-[80px_1fr] gap-4">
              <div></div> {/* 空白占位，保持对齐 */}
              <div className='flex justify-center gap-4'>
                <Button type="submit" variant="outline" onClick={() => { navigate(-1); }}>{t("teacher.prev")}</Button>
                <Button
                  type="submit"
                  variant="default"
                  className='bg-primary'
                  onClick={() => { onSubmit(); }}
                  disabled={!isFormComplete}
                  loading={isLoading}
                >{t("component.done")}</Button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* 成功弹窗 */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('common.success')}</DialogTitle>
            {/* <DialogDescription>
              题目创建已完成
            </DialogDescription> */}
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:justify-center">
            <Button
              variant="outline"
              onClick={() => {
                setShowSuccessDialog(false);
                navigate(ROUTE.TEACHER.LIBRARY_EDIT);
              }}
            >
              {t('teacher.goedit')}
            </Button>
            <Button
              className="bg-primary hover:bg-primary/90"
              onClick={() => {
                setShowSuccessDialog(false);
                // If coming from homework assignment flow, resume it
                if (state.resumeHomeworkState) {
                  navigate(ROUTE.TEACHER.HOMEWORK_MANUAL_EDIT, {
                    state: {
                      ...state.resumeHomeworkState,
                      // Pass the new question ID to fetch and add on the ManualEdit page
                      newQuestionId: createdQuestionId,
                      // Ensure dueDate is preserved as Date object
                      dueDate: state.resumeHomeworkState.dueDate instanceof Date
                        ? state.resumeHomeworkState.dueDate
                        : new Date(state.resumeHomeworkState.dueDate),
                    }
                  });
                } else {
                  navigate(ROUTE.TEACHER.HOMEWORK_ASSIGN);
                }
              }}
            >
              {state.resumeHomeworkState ? t('teacher.goresume') || 'Resume Assignment' : t('teacher.goassign')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ContentLayout>
  )
};

const QuestionEditPage = () => {
  const state = useLocation().state as QuestionEditPageProps | null;
  const navigate = useNavigate();

  const isValidState = (s: any): s is QuestionEditPageProps => {
    return !!s && typeof s.isEditMode === 'boolean';
  };

  useEffect(() => {
    if (!isValidState(state)) {
      navigate(ROUTE.TEACHER.LIBRARY_EDIT, { replace: true });
    }
  }, [state, navigate]);

  if (!isValidState(state)) {
    return <LoadingOverlay isLoading={true} />;
  }

  return <QuestionEditPageInner state={state} />;
};

export default QuestionEditPage;